"use client";

import {
  useQuery as useInsiderMembershipInformationQuery,
  useInvalidateQuery as useInsiderMembershipInformationInvalidateQuery,
} from "@faire/web-api--source/api/membership/retailerToken/get-hooks";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IInsiderMembershipInformation } from "@faire/web-api--source/indigofair/insider/IInsiderMembershipInformation";
import { useEffect } from "react";

import { InsiderMembershipMutated } from "@faire/retailer/events";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getInsiderMemershipInformation } from "@faire/retailer/serialized-data/getInsiderMembershipInformation";

import { useRetailerStore } from "../../stores/domain/RetailerStore";

interface IUseInsiderMembershipInformationResult {
  /** returns the InsiderMembershipInformation of the current session's retailer */
  insiderMembershipInformation: IInsiderMembershipInformation | undefined;
  /** Indicates if the Insider membership information is currently being loaded. */
  isInsiderMembershipInformationFetching: boolean;

  /** The threshold values for Insider membership */
  thresholds: {
    threshold1: IFaireMoney | undefined;
    threshold2: IFaireMoney | undefined;
  };
  /** The number of threshold values for Insider membership */
  thresholdCounts: number;
}

/** This hook fetches InsiderMembershipInformation */
export function useInsiderMembershipInformation(): IUseInsiderMembershipInformationResult {
  const { retailerToken } = useRetailerStore(["retailerToken"]);
  const isLoggedInRetailer = useIsLoggedInRetailer();

  //  serialized data is valid until it gets mutated. Hence, it will not become stale until it is invalidated
  const {
    data: insiderMembershipInformation,
    isFetching: isInsiderMembershipInformationFetching,
  } = useInsiderMembershipInformationQuery(retailerToken ?? "", {
    initialData: getInsiderMemershipInformation,
    staleTime: Infinity, // BE session expiration time
    enabled: !!retailerToken, // query only runs when there's a retailerToken available
  });

  const invalidateInsiderMembershipInformationQuery =
    useInsiderMembershipInformationInvalidateQuery();

  useEffect(() => {
    let invalidationFn: () => Promise<void> | undefined;
    if (isLoggedInRetailer && retailerToken) {
      invalidationFn = () =>
        invalidateInsiderMembershipInformationQuery(retailerToken);
      invalidateOnEvents(invalidationFn, retailerToken);
    }
    return () => {
      if (invalidationFn !== undefined) {
        invalidateAppEvents(invalidationFn);
      }
    };
  }, [
    retailerToken,
    isLoggedInRetailer,
    invalidateInsiderMembershipInformationQuery,
  ]);

  const threshold1 =
    insiderMembershipInformation?.threshold_values?.THRESHOLD_1;
  const threshold2 =
    insiderMembershipInformation?.threshold_values?.THRESHOLD_2;

  const thresholdCounts = Object.keys(
    insiderMembershipInformation?.threshold_values ?? {}
  ).length;

  return {
    insiderMembershipInformation,
    isInsiderMembershipInformationFetching,
    thresholds: {
      threshold1,
      threshold2,
    },
    thresholdCounts,
  };
}

const invalidateAppEvents = (invalidationFn: () => void) => {
  InsiderMembershipMutated.unsubscribe(invalidationFn);
};

/** Auxiliary function to handle invalidation of the Insider membership information query on certain AppEvents. It saves the last callback subscribed and retailer token used to avoid having multible callbacks subscribed at the same time. */
const invalidateOnEvents: (
  invalidationCallback: () => void,
  token: string
) => void = (() => {
  let subscribedInvalidationCallback: (() => void) | null = null;
  let retailerToken: string | null = null;

  return (invalidationCallback, token) => {
    if (token !== retailerToken) {
      if (subscribedInvalidationCallback) {
        invalidateAppEvents(invalidationCallback);
      }

      InsiderMembershipMutated.subscribe(invalidationCallback);

      subscribedInvalidationCallback = invalidationCallback;
      retailerToken = token;
    }
  };
})();
